<script setup lang="ts">import { ref as _ref, toRef as _toRef } from 'vue';

import { useWindowScroll } from '@vueuse/core'

import Customizer from '@consumer/modules/multiPlaceGiftIdea/Customizer.vue'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import type { GiftlyDesign, MultiplaceGift } from '@consumer/types/serializers'
import PopUpCTA from '@consumer/modules/multiPlaceGiftIdea/PopUpCTA.vue'
import { sleep } from '@shared/timers'
import { useSharedData } from '@consumer/composables/sharedData'
import { usePrimaryPlace } from './composables'

withDefaults(defineProps<{
  showSecondaryGiftlyImage: boolean
  themeColor?: string
  gift: GiftTemplate
  multiPlaceGiftIdea: MultiplaceGift
  giftlyDesigns: GiftlyDesign[]
  isEditing?: boolean
  isEditingField?: boolean
}>(), { isEditing: false, })

const emit = defineEmits<{ setAmount: [amount: number] }>()

const selectedDesign = defineModel<GiftlyDesign>('selectedDesign', { required: true })

const { y: windowY } = useWindowScroll()
let showMobileDesignPicker = _ref(false)
watchEffect(() => {
  if (windowY.value > 130) {
    showMobileDesignPicker.value = true
  }
})

const primaryPlace = usePrimaryPlace()
let isDesktopPopUpCTAOpen = _ref(Boolean(primaryPlace?.value))
const onClosePopUpCTA = () => {
  sleep(200).then(() => {
    isDesktopPopUpCTAOpen.value = false
  })
}

const __$temp_1 = (useSharedData()),
  currentTheme = _toRef(__$temp_1, 'currentTheme');

const setAmount = (amount: number) => {
  emit('setAmount', amount)
}
</script>

<template>
  <div
    class="absolute top-0 bottom-28"
    :style="{
      left: 'calc(50% + 70px)'
    }"
  >
    <FavoriteButton
      v-show="!showSecondaryGiftlyImage"
      class="!<lg:hidden absolute z-1 top-110 left-1/2 transform -translate-x-1/2"
      :favoritable="multiPlaceGiftIdea"
      :themeColor="themeColor"
    />
    <div
      class="hidden lg:flex flex-col pb-0 z-1 transform transition-all ease-in-out duration-300"
      :style="{
        width: '400px',
        position: 'sticky',
        top: (showSecondaryGiftlyImage ? '120px' : '0'),
        transition: 'top 0.3s',
        paddingTop: showSecondaryGiftlyImage ? '0' : isDesktopPopUpCTAOpen ? '20px' : '80px',
      }"
    >
      <PopUpCTA
        v-show="!showSecondaryGiftlyImage"
        :gift="gift"
        class="mb-5"
        @close="onClosePopUpCTA"
      />

      <div
        v-show="showSecondaryGiftlyImage"
        class="p-10 pb-20 rounded-2xl transition ease-in-out duration-300 w-full relative"
        :style="{
          backgroundColor: themeColor,
          boxShadow: '0px 0px 15px 0px rgba(0, 0, 0, 0.15)'
        }"
      >
        <GiftlyV3Image :gift="gift" :inCustomizer="true" class="w-full"/>
        <FavoriteButton
          class="absolute z-1 top-7 right-7"
          :favoritable="multiPlaceGiftIdea"
          :themeColor="themeColor"
          :label="false"
        />
      </div>

      <div>
        <Customizer
          v-model:selectedDesign="selectedDesign"
          :multiPlaceGiftIdea="multiPlaceGiftIdea"
          :gift="gift"
          :giftlyDesigns="giftlyDesigns"
          class="bg-white"
          :class="{ '-mt-10': showSecondaryGiftlyImage }"
          :isEditing="isEditing"
          :isEditingField="isEditingField"
          :showSecondaryGiftlyImage="showSecondaryGiftlyImage"
          @setAmount="setAmount"
        />
      </div>
    </div>
  </div>

  <div
    class="fixed w-full bottom-0 z-2 lg:hidden transform transition-all ease-in-out duration-300"
    :class="[showMobileDesignPicker ? 'translate-y-0' : 'translate-y-full']"
  >
    <Customizer
      v-model:selectedDesign="selectedDesign"
      :multiPlaceGiftIdea="multiPlaceGiftIdea"
      :gift="gift"
      :giftlyDesigns="giftlyDesigns"
      :isEditing="isEditing"
      :isEditingField="isEditingField"
      :class="currentTheme === 'holidays' && 'pt-8'"
      :showSecondaryGiftlyImage="showSecondaryGiftlyImage"
      @setAmount="setAmount"
    />
  </div>
</template>
